/* This bar delegates purchases to Wonder's existing product forms. */
if (!customElements.get('garrnish-supply-sticky')) {
  customElements.define('garrnish-supply-sticky', class extends HTMLElement {
    async connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      await customElements.whenDefined('garrnish-supply');
      if (signal.aborted) return;
      this.panel = document.getElementById(this.dataset.panelId);
      if (this.panel && !this.panel.offers) {
        // The panel starts late on pages where it sits lower down.
        await new Promise(resolve => this.panel.addEventListener('garrnish:supply-ready', resolve, { once: true, signal }));
        if (signal.aborted) return;
      }
      if (!this.panel?.offers || !this.panel?.dogs) return;
      this.section = this.panel.closest('.gs-product');
      this.choice = this.panel.offer;
      this.pickers = [...this.querySelectorAll('details')];
      this.picker = this.querySelector('[data-offer-picker]');
      this.submit = this.querySelector('[data-sticky-submit]');
      this.defaultCta = this.querySelector('[data-sticky-label]').textContent;
      const options = { signal };
      this.panel.addEventListener('garrnish:supply-change', () => {
        this.choice = this.panel.offer;
        this.render();
      }, options);
      for (const picker of this.pickers) {
        picker.querySelector('summary').addEventListener('click', () => {
          this.pickers.filter(other => other !== picker).forEach(other => this.closePicker(other));
        }, options);
      }
      this.querySelectorAll('[data-sticky-choice]').forEach(button => button.addEventListener('click', () => {
        this.choice = button.dataset.stickyChoice;
        this.panel.querySelector(`[data-offer="${this.choice}"] input`).click();
        this.picker.open = false;
        this.render();
        this.picker.querySelector('summary').focus({ preventScroll: true });
      }, options));
      this.submit.addEventListener('pointerdown', () => window.GarrnishCart?.prefetch?.(), options);
      this.submit.addEventListener('click', () => {
        this.panel.update(false);
        const button = this.sourceButton();
        if (!button.disabled && button.getAttribute('aria-disabled') !== 'true') button.click();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.pickers.filter(picker => picker.open).forEach(picker => this.closePicker(picker, true));
      }, options);
      document.addEventListener('click', event => {
        this.pickers.filter(picker => !picker.contains(event.target)).forEach(picker => this.closePicker(picker));
      }, options);
      // Mirror loading/unavailable state from the native AJAX buttons.
      this.observer = new MutationObserver(() => this.render());
      for (const form of [this.panel.form]) {
        this.observer.observe(form.querySelector('[type="submit"]'), { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
      }
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateVisibility(); });
      };
      window.addEventListener('scroll', this.onScroll, { ...options, passive: true });
      window.addEventListener('resize', () => this.render(), options);
      this.render();
      this.updateVisibility();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame);
      document.body.classList.remove("garrnish-buy-bar-on");
    }
    closePicker(picker, focus = false) {
      picker.open = false;
      if (focus) picker.querySelector('summary').focus({ preventScroll: true });
    }
    sourceButton() {
      return this.panel.form.querySelector('[type="submit"]');
    }
    describe(choice, sizes = [this.panel.size], once = false) {
      const p = this.panel;
      const row = typeof choice === 'string' ? p.offers[p.size][choice] : choice;
      const price = once ? {subtotal:p.variant()?.price || window.HouseholdPlanner.PRICE.once,saving:0} : row ? p.pricing(row,sizes) : null;
      const dog = p.dogs[sizes[0]];
      const dogs = sizes.length > 1 ? this.dataset.householdLabel : `${dog.label} (${dog.weight.replace(/[–—]/g, '-')})`;
      return {
        title: once ? p.h('once_title') : row ? p.t('weeks',row) : p.h('pending'),
        dogs,
        savingPercent: price?.saving || 0,
        volume: Boolean(price?.volume),
        saving: price?.saving > 0 ? p.h('saving',{percent:price.saving}) : '',
        total: price ? p.money(price.subtotal) : '',
        compare: !once && row && price?.saving > 0 ? p.money(row.boxes * window.HouseholdPlanner.PRICE.once) : ''
      };
    }
    renderSummary(container, offer, tier) {
      container.querySelector('[data-sticky-title]').textContent = offer.title;
      container.querySelector('[data-sticky-dogs]').textContent = offer.dogs;
      container.querySelector('[data-sticky-total]').textContent = offer.total;
      const compare = container.querySelector('[data-sticky-compare]');
      compare.textContent = offer.compare;
      compare.hidden = !offer.compare;
      const pill = container.querySelector('[data-sticky-saving]');
      pill.textContent = offer.saving;
      pill.hidden = !offer.saving;
      pill.dataset.tier = tier;
      pill.dataset.savingsPercent = offer.savingPercent;
      pill.dataset.volume = String(offer.volume);
    }
    render() {
      const p = this.panel;
      // Measuring the section forces a page layout; one check per frame is enough while the panel updates.
      if (this.onScroll) this.onScroll(); else this.updateVisibility();
      if(!p.selectionConfirmed)return;
      const stickyLabel=this.querySelector('[data-sticky-label]');
      const ctaLabel=p.ctaHTML(this.defaultCta,true);
      if(stickyLabel.innerHTML!==ctaLabel)stickyLabel.innerHTML=ctaLabel;
      this.submit.dataset.cartAction=p.cartAction;
      const once=p.purchaseMode==='once';
      const household = p.sizes?.length > 1 || once;
      this.picker.hidden = household;
      const householdSummary = this.querySelector('[data-household-sticky]');
      householdSummary.hidden = !household;
      if (household) {
        this.closePicker(this.picker);
        this.renderSummary(householdSummary,this.describe(p.activeRow,p.sizes,once),'household');
        const source = this.sourceButton();
        this.submit.disabled = source.disabled || p.busy || source.getAttribute('aria-disabled') === 'true';
        this.submit.setAttribute('aria-busy', String(Boolean(p.busy)));
        return;
      }
      this.renderSummary(this.picker.querySelector('summary'),this.describe(this.choice),this.choice);
      for (const button of this.querySelectorAll('[data-sticky-choice]')) {
        this.renderSummary(button,this.describe(button.dataset.stickyChoice),button.dataset.stickyChoice);
        button.setAttribute('aria-pressed', String(button.dataset.stickyChoice === this.choice));
      }
      const source = this.sourceButton();
      this.submit.disabled = source.disabled || p.busy || source.getAttribute('aria-disabled') === 'true';
      this.submit.setAttribute('aria-busy', String(Boolean(p.busy)));
    }
    updateVisibility() {
      // Keep purchase controls available above the product once the cart has items.
      const rect=this.section.getBoundingClientRect();
      const wasHidden = this.hidden;
      this.hidden = !this.panel.selectionConfirmed || (!this.panel.cartHasItems && rect.top >= window.innerHeight * .85);
      if (wasHidden !== this.hidden) {
        // A body class instead of body:has(.gss:not([hidden])): that selector made the browser re-check the whole page on every class change.
        document.body.classList.toggle("garrnish-buy-bar-on", !this.hidden);
        document.dispatchEvent(new CustomEvent("garrnish:sticky-visibility"));
      }
      if (this.hidden) this.pickers.forEach(picker => this.closePicker(picker));
    }
  });
}
